import { useEffect, useRef, useState, type FormEvent } from "react";
import Button from "./Button";
import { SUPPORT_EMAIL } from "@/lib/data";
import { useLocale } from "@/i18n/useLocale";
import { supportFor } from "@/content/support";

function buildMailto(name: string, fromEmail: string, message: string): string {
  const subject = `[Support] Quick message — ${name || "trader enquiry"}`;
  const body = [`Name: ${name}`, `Email: ${fromEmail}`, "", message].join("\n");
  const params = new URLSearchParams({ subject, body });
  return `mailto:${SUPPORT_EMAIL}?${params.toString().replace(/\+/g, "%20")}`;
}

function ChatBubble(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a8 8 0 0 1-11.6 7.16L3 21l1.84-6.4A8 8 0 1 1 21 12z" />
      <path d="M8 12h.01" />
      <path d="M12 12h.01" />
      <path d="M16 12h.01" />
    </svg>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 6L18 18" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

export default function SupportWidget(): JSX.Element {
  const t = supportFor(useLocale());
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e: MouseEvent): void {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (buttonRef.current?.contains(t)) return;
      setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    window.location.href = buildMailto(name, fromEmail, message);
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setOpen(false);
    }, 1800);
  }

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t.widget.dialogLabel}
          className="glass-card glass-card-glow fixed z-[60] flex flex-col p-4 md:p-5 animate-page-in"
          style={{
            right: "max(16px, env(safe-area-inset-right, 16px))",
            bottom: "calc(16px + 64px + 12px)",
            width: "min(360px, calc(100vw - 32px))",
          }}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-display font-semibold text-[18px] leading-tight">
                {t.widget.heading}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.widget.closePanel}
              className="text-fg-muted hover:text-fg p-1 -mr-1"
            >
              <CloseIcon />
            </button>
          </div>
          <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
            <input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-bg-2 border border-border rounded-md px-3 py-2.5 text-fg font-sans text-sm outline-none focus:border-brand-emphasis"
            />
            <input
              placeholder="Email"
              type="email"
              required
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              className="bg-bg-2 border border-border rounded-md px-3 py-2.5 text-fg font-sans text-sm outline-none focus:border-brand-emphasis"
            />
            <textarea
              rows={4}
              required
              placeholder="How can we help?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-bg-2 border border-border rounded-md px-3 py-2.5 text-fg font-sans text-sm outline-none focus:border-brand resize-y"
            />
            <Button variant="primary" type="submit" className="w-full justify-center">
              {sent ? <>Email opened ✓</> : <>Send message</>}
            </Button>
            <div className="font-mono text-fg-muted text-[10px] text-center tracking-[0.06em]">
              OPENS YOUR EMAIL CLIENT · REPLY &lt; 4 H · 24/7
            </div>
          </form>
        </div>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t.widget.closeTrigger : t.widget.openTrigger}
        aria-expanded={open}
        className="fixed z-[60] bg-brand text-white rounded-full shadow-[0_10px_30px_-6px_rgba(0,0,0,0.55),0_0_0_4px_rgb(var(--brand)/0.18)] hover:scale-[1.04] active:scale-[0.98] transition-transform flex items-center justify-center"
        style={{
          right: "max(16px, env(safe-area-inset-right, 16px))",
          bottom: "max(16px, env(safe-area-inset-bottom, 16px))",
          width: 56,
          height: 56,
        }}
      >
        {open ? <CloseIcon /> : <ChatBubble />}
      </button>
    </>
  );
}
